import { useCardImageIndex } from '../../hooks/useCardImages'
import { useCollectionScope } from '../../hooks/useCollectionScope'
import { faceImageURL } from '../../api/cardImages'
import { tileArtCrop } from '../../lib/cardArtCrop'

/**
 * Art tiles for the popular commanders. One batched image lookup for all of
 * them (the same POST /cards/images path the deck views use). useCardImages
 * keys on the whole name-set per call site, so this does not share cache
 * entries with other consumers (e.g. the home page's own image lookups) even
 * for a commander both sets include.
 *
 * A tile whose art has not resolved still renders and is still clickable - the
 * name is the payload, the art is decoration.
 */
export default function PopularGrid({
  names,
  onSelect,
}: {
  names: string[]
  onSelect: (commander: string, partner: string | null) => void
}) {
  const scope = useCollectionScope()
  const { getInfo } = useCardImageIndex(names, scope)

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {names.map((name, i) => {
        const info = getInfo(name)
        const url = info ? faceImageURL(info, { large: false }) : undefined
        return (
          <button
            key={name}
            type="button"
            onClick={() => onSelect(name, null)}
            // Two columns on a phone means twelve tiles is six rows of scrolling
            // before the entry cards below them are reachable. Show the top six
            // there; the full set returns at sm, where they fit three across.
            className={`group relative aspect-[5/4] rounded-xl overflow-hidden border border-white/10
                       hover:border-purple-500/60 transition-colors text-left
                       ${i >= 6 ? 'hidden sm:block' : ''}`}
          >
            {url ? (
              <span
                aria-hidden="true"
                className="absolute inset-0 transition-transform duration-300 group-hover:scale-105"
                style={tileArtCrop(url)}
              />
            ) : (
              <span className="absolute inset-0 bg-gradient-to-br from-surface-2 to-gray-950" />
            )}
            <span
              aria-hidden="true"
              className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, rgba(3,7,18,0) 45%, rgba(3,7,18,.93) 100%)' }}
            />
            <span className="absolute inset-x-0 bottom-0 p-2.5 text-[12.5px] font-semibold text-white leading-tight"
                  style={{ textShadow: '0 1px 6px rgba(0,0,0,.9)' }}>
              {name}
            </span>
          </button>
        )
      })}
    </div>
  )
}
